import { useEffect, useState } from 'react';
import { reservationService } from '../clients/ApiClients';
import { useSyncedPendingReservationCount } from '../sync/reservationHooks';

/**
 * How many reservation requests are waiting on a manager, for the
 * sidebar's badge (#642).
 *
 * Every page mounts the sidebar, so the count is cached at module level
 * for a minute rather than fetched per page (the cost #561 is about). A
 * decision made on the detail page calls `invalidatePendingReservationCount`
 * so the badge does not lag the queue the manager is working through.
 */
const FRESH_FOR_MS = 60 * 1000;
let cached: { count: number; at: number } | null = null;
let inFlight: Promise<number> | null = null;
const listeners = new Set<(count: number) => void>();

const fetchCount = (): Promise<number> => {
  if (inFlight) return inFlight;
  inFlight = reservationService
    .listReservations({ status: 'pending', perPage: 1 })
    .then((r) => {
      cached = { count: r.data.total, at: Date.now() };
      listeners.forEach((l) => l(r.data.total));
      return r.data.total;
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
};

export const invalidatePendingReservationCount = (): void => {
  cached = null;
  if (listeners.size) fetchCount().catch(() => undefined);
};

export const usePendingReservationCount = (enabled: boolean): number => {
  const [count, setCount] = useState(cached?.count ?? 0);
  // From the sync engine's copy when there is one (#561): no request on
  // any page, and a decision made anywhere moves the badge at once. The
  // copy holds the same associations the list endpoint scopes to.
  const synced = useSyncedPendingReservationCount();
  const haveSynced = synced !== null;

  useEffect(() => {
    if (!enabled || haveSynced) return;
    listeners.add(setCount);
    if (!cached || Date.now() - cached.at > FRESH_FOR_MS) {
      fetchCount().catch(() => {
        // The badge is a convenience; the queue itself reports errors.
      });
    } else {
      setCount(cached.count);
    }
    return () => {
      listeners.delete(setCount);
    };
  }, [enabled, haveSynced]);

  if (!enabled) return 0;
  return synced ?? count;
};
